import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useEstablishments } from "@/context/EstablishmentContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Wand2, Loader2, Copy, Check, ListPlus, FlaskConical } from "lucide-react";

export default function Generators() {
  const { current } = useEstablishments();
  if (!current) return <div className="p-8 text-slate-500">Aucun établissement sélectionné.</div>;
  return <GeneratorsInner key={current.id} est={current} />;
}

function GeneratorsInner({ est }) {
  const [gens, setGens] = useState([]);
  const [pages, setPages] = useState([]);
  const [type, setType] = useState("title");
  const [pageUrl, setPageUrl] = useState("");
  const [instr, setInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/ai/generators").then((r) => setGens(r.data));
    api.get(`/establishments/${est.id}/site-audit`).then((r) => setPages((r.data.latest?.pages || []).map((p) => p.url)));
    api.get(`/establishments/${est.id}/ai/generations`).then((r) => setHistory(r.data));
  }, [est.id]);

  const g = gens.find((x) => x.type === type);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/establishments/${est.id}/ai/generate`, { type, page_url: g?.needs_page ? pageUrl || null : null, instructions: instr || null });
      setResult(data); setHistory((h) => [data, ...h]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addToPlan = async (r) => {
    try {
      await api.post(`/establishments/${est.id}/ai/generated-task`, { title: `${gens.find((x) => x.type === r.type)?.label || r.type}${r.page_url ? ` — ${r.page_url}` : ""} (généré)`, correction: r.content, page_url: r.page_url });
      toast.success("Ajouté au plan d'action");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Wand2 className="w-7 h-7 text-indigo-600" /> Générateurs</h1>
      <p className="text-slate-500 text-sm mt-1">Contenus prêts à l'emploi, générés à partir des données réellement mesurées de {est.name}. À valider avant publication.</p>
      {est.is_demo && <div className="mt-4 rounded-xl bg-amber-50/60 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="generators-demo-notice"><FlaskConical className="w-3.5 h-3.5" /> Données de démonstration.</div>}

      <div className="mt-6 grid gap-2 sm:grid-cols-4" data-testid="generator-types">
        {gens.map((x) => (
          <button key={x.type} onClick={() => { setType(x.type); setResult(null); }} data-testid={`generator-type-${x.type}`}
            className={`text-left px-3.5 py-3 rounded-xl border text-sm font-medium transition-colors ${type === x.type ? "border-indigo-400 bg-indigo-50 text-indigo-800" : "bg-white border-slate-200 hover:border-indigo-300 text-slate-700"}`}>{x.label}</button>
        ))}
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        {g?.needs_page && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Page concernée</label>
            <select value={pageUrl} onChange={(e) => setPageUrl(e.target.value)} data-testid="generator-page-select" className="mt-1 w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
              <option value="">Page d'accueil{pages.length === 0 ? " (lancez « Analyser mon site » pour lister les pages)" : ""}</option>
              {pages.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Consignes (optionnel)</label>
          <Textarea value={instr} onChange={(e) => setInstr(e.target.value)} rows={2} maxLength={500} placeholder="Ex. : mettre en avant le brunch du dimanche, ton chaleureux…" data-testid="generator-instructions" className="mt-1 rounded-lg" />
        </div>
        <Button onClick={run} disabled={busy || !g} data-testid="generator-run-button" className="bg-indigo-600 hover:bg-indigo-700 rounded-xl">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />} {busy ? "Génération…" : `Générer : ${g?.label || ""}`}
        </Button>
      </div>

      {result && (
        <div className="mt-5 bg-white rounded-2xl border border-indigo-200 p-5" data-testid="generator-result">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Correction proposée · {gens.find((x) => x.type === result.type)?.label}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(result.content); setCopied(true); setTimeout(() => setCopied(false), 1500); }} data-testid="generator-copy" className="h-8 rounded-lg">{copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Copier</Button>
              <Button size="sm" onClick={() => addToPlan(result)} data-testid="generator-add-task" className="h-8 rounded-lg bg-blue-600 hover:bg-blue-700"><ListPlus className="w-4 h-4 mr-1" /> Ajouter au plan d'action</Button>
            </div>
          </div>
          <div className="prose prose-sm prose-slate max-w-none prose-pre:bg-slate-50 prose-pre:text-slate-800 prose-pre:border"><ReactMarkdown>{result.content}</ReactMarkdown></div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-lg font-semibold mb-2">Historique ({history.length})</h2>
          <div className="space-y-2" data-testid="generator-history">
            {history.slice(0, 10).map((h) => (
              <button key={h.id} onClick={() => setResult(h)} data-testid={`generator-history-${h.id}`} className="w-full text-left bg-white rounded-xl border border-slate-200 px-4 py-2.5 text-sm hover:border-indigo-300">
                <span className="font-medium text-slate-800">{gens.find((x) => x.type === h.type)?.label || h.type}</span>
                <span className="text-slate-400"> · {new Date(h.created_at).toLocaleString("fr-FR")}{h.page_url ? ` · ${h.page_url}` : ""}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
